/**
 * The design system's atoms.
 *
 * Everything visual that repeats lives here so a screen never hand-rolls a tag or
 * a frame. Two of these carry real rules rather than styling:
 *
 * - `AlertTag` owns the redundant non-colour encoding. The palette is mono, so
 *   danger and emphasis share a hue and colour alone cannot carry the meaning
 *   (WCAG 1.4.1). Every alert tag therefore takes a `kind` and prefixes the glyph
 *   for it.
 * - `Freshness` refuses to let a stale value read as current. It is the only
 *   sanctioned way to render an `Observed<T>`.
 */
import { forwardRef, type ReactNode } from "react";
import {
  assetStatusDescriptions,
  formatFreshness,
  formatSnapshotFreshness,
  formatMoney,
  formatRate,
  type AssetStatusChip,
  type Freshness as FreshnessState,
  type Observed,
  type QueueRow,
  type ScopeMarker,
  type SnapshotEvent,
} from "@truckbuddy/contract";

/**
 * The hairline frame with four "+" registration marks. The signature element.
 *
 * Takes a `ref` so a caller can scroll it into view — "Locate on heat map" needs
 * exactly that, and without the ref it has to hang the scroll target on a wrapper
 * element instead.
 */
export const Blueprint = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    className?: string;
    surface?: boolean;
    style?: React.CSSProperties;
  } & Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "className" | "style">
>(function Blueprint({ children, className = "", surface = false, style, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={`blueprint ${surface ? "panel-surface" : ""} ${className}`.trim()}
      style={style}
      {...rest}
    >
      <i className="corner tl" />
      <i className="corner tr" />
      <i className="corner bl" />
      <i className="corner br" />
      {children}
    </div>
  );
});

export type TagTone = "accent" | "accent-2" | "neutral" | "outline";

export function Tag({
  tone = "neutral",
  children,
  className = "",
  style,
  title,
}: {
  tone?: TagTone;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Hover explanation. Used for the caveat behind an abbreviated tag. */
  title?: string;
}) {
  return (
    <span className={`tag tag-${tone} ${className}`.trim()} style={style} title={title}>
      {children}
    </span>
  );
}

/**
 * The things the design marks with an alert tag, each with its own glyph.
 *
 * The handoff flagged a live design-system decision here: one glyph (▲) carried
 * every meaning, and either a reserved alert colour or a distinct glyph set was
 * needed. Distinct glyphs came first. The colour came with the UI renovation
 * (TRBUD-439, Eden, 21 October 2026: "make one a red and another orange"): RED
 * is DOWN, ORANGE is OVERDUE, and the glyphs stay, so colour is never the only
 * signal. Every other kind keeps the accent-900 emphasis.
 *
 * ── `notice`: emphasis that is NOT down and NOT overdue ─────────────────
 * Before the hues, several tags borrowed `down` or `overdue` for the glyph alone:
 * a work order's DRAFT, DELETED and PAUSED states, ON LOAD, an invoice whose
 * lines disagree with its total, a bill on a MaintainX job. With the hues those
 * would have turned red or orange, and red must mean a unit is out of service,
 * orange that a date has passed. They take `notice`: the plain `.tag-alert`
 * emphasis they always had, with `!`. Chosen because it is the plain "take note"
 * mark; it is not one of the design's status glyphs (▲ overdue, ■ down, ◆ in
 * progress, ○ waiting, ● done), not another kind's (✕ ? ◷), not the field
 * refusal's ⚠ or the attention flag's ⚑, and it is ASCII, so it draws in every
 * face the app can be set in, the Matrix mono included.
 */
const ALERT_GLYPH = {
  overdue: "▲",
  down: "■",
  notice: "!",
  blacklisted: "✕",
  lowConfidence: "?",
  stale: "◷",
} as const;

export type AlertKind = keyof typeof ALERT_GLYPH;

/**
 * The hue class each coloured kind adds to `.tag-alert` (`styles/industry.css`).
 * Written out whole rather than built from the kind, so a search finds them;
 * `styles/alert-hues.test.ts` holds each to a rule in the stylesheet.
 *
 * DOWN AND OVERDUE ONLY, and `primitives.test.tsx` fails if a third kind is
 * added here or if any other file names these classes: a tag that is neither
 * takes `notice`, never a hue.
 */
const ALERT_HUE_CLASS: Partial<Record<AlertKind, string>> = {
  down: "tag-alert-down",
  overdue: "tag-alert-overdue",
};

/** The classes an `AlertTag` of this kind carries, before any the caller adds. */
export function alertTagClassName(kind: AlertKind): string {
  const hue = ALERT_HUE_CLASS[kind];
  return hue === undefined ? "tag tag-alert" : `tag tag-alert ${hue}`;
}

export function AlertTag({
  kind,
  children,
  className = "",
  title,
}: {
  kind: AlertKind;
  children: ReactNode;
  className?: string;
  /** Hover explanation — typically the provenance of a derived state. */
  title?: string;
}) {
  return (
    <span className={`${alertTagClassName(kind)} ${className}`.trim()} title={title}>
      <span aria-hidden="true" style={{ marginRight: 5 }}>
        {ALERT_GLYPH[kind]}
      </span>
      {children}
    </span>
  );
}

/**
 * The one In-house / Vendor chip, at every width (TRBUD-439; design v5 #14,
 * Brett row 10: make the two "more obvious"). In-house is a filled ink chip with
 * ⌂, Vendor an outlined accent chip with ⧉: fill against outline and a glyph
 * each, not a third status colour, because red and orange mean down and overdue.
 *
 * Takes the queue's own `pipelineKind` so a row passes what it already has. The
 * words are "In-house" and "Vendor"; the stylesheet sets them in capitals.
 */
export type PipelineKind = QueueRow["pipelineKind"];

const PIPELINE_CHIP: Record<PipelineKind, { glyph: string; word: string; className: string }> = {
  INTERNAL: { glyph: "⌂", word: "In-house", className: "tag tag-pipeline tag-pipeline-inhouse" },
  VENDOR: { glyph: "⧉", word: "Vendor", className: "tag tag-pipeline tag-pipeline-vendor" },
};

export function PipelineTag({ kind, className = "" }: { kind: PipelineKind; className?: string }) {
  const chip = PIPELINE_CHIP[kind];
  return (
    <span className={`${chip.className} ${className}`.trim()}>
      <span aria-hidden="true">{chip.glyph}</span>
      {chip.word}
    </span>
  );
}

/**
 * The asset status chip, in one place.
 *
 * Lives here rather than in a screen because the list and the record both render
 * it, and H6's mapping is precisely the thing that must not exist twice: two
 * copies is how the Assets table and the asset record end up disagreeing about
 * whether a unit is down. The `title` carries the provenance — this chip is a
 * derived display state, not `asset.status`, and says so on hover.
 *
 * ── The hover also EXPLAINS the stored status ────────────────────────
 * Requested by Eden on 27 August 2026, when `disposed` was added. Four of the
 * five stored statuses sound alike — inactive, decommissioning, retired,
 * disposed all read as "not working right now" — and the distinction that
 * matters is ownership: `retired` we still own, `disposed` has left the company.
 * A chip that shows the raw value without saying what it means invites the wrong
 * reading, so the description travels with it.
 *
 * The text lives in `assetStatusDescriptions` in `packages/contract`, beside the
 * vocabulary the CHECK constrains — not here. A copy in the front end is a copy
 * that drifts from the database.
 */
/**
 * "Draft" on a row only its author can see (TRBUD-337 D1). The Queue and the
 * asset record list a viewer's own drafts, and no count includes them, so the
 * tag is what tells the author why the row is not in the figures above it. The
 * same outline tag the asset record has always used.
 */
export function DraftTag() {
  return (
    <Tag tone="outline" title="A draft: only you can see it, and no count includes it until you release it to the shop.">
      Draft
    </Tag>
  );
}

export function AssetStatusTag({ status }: { status: AssetStatusChip }) {
  const described = (assetStatusDescriptions as Record<string, string | undefined>)[
    status.dbStatus
  ];
  const provenance = described ?? status.label;
  // Parked (TRBUD-337 D5): inactive and decommissioning are lifecycle facts,
  // not breakdowns, so they read as an outline tag rather than a red alert.
  if (status.display === "testing" || status.display === "inactive" || status.display === "decommissioning") {
    return (
      <Tag tone="outline" title={provenance}>
        {status.label}
      </Tag>
    );
  }
  if (status.display === "in_service") {
    return (
      <Tag tone="neutral" title={provenance}>
        {status.label}
      </Tag>
    );
  }
  return (
    <AlertTag kind="down" title={provenance}>
      {status.label}
    </AlertTag>
  );
}

/**
 * The build gate a payload carries, rendered where the user can see it.
 *
 * `discovery` means the screen has no backing tables and its rows are seeded demo
 * values. Saying so is not a caveat to be tucked away — a number nobody can trace
 * is worse than a blank, and someone will otherwise act on it.
 *
 * **The label is a short badge and the explanation is the tooltip.** It used to
 * shout `DISCOVERY — UNBACKED` in capitals; the marker still says exactly the
 * same thing, in a word a reader can take in beside a heading rather than one
 * that competes with it. The full note is on `title`, and `ScopeNote` below puts
 * the same text behind a disclosure, so nothing is hidden — it is just no longer
 * the loudest thing on the page.
 */
export function ScopeTag({ meta }: { meta: ScopeMarker | undefined }) {
  if (!meta) return null;
  const label =
    meta.scope === "pilot" ? "Pilot" : meta.scope === "committed" ? "Built" : "Sample data";
  return (
    <span className="tag tag-scope" title={meta.note ?? undefined}>
      {label}
      {meta.flags.length > 0 ? ` · ${meta.flags.join(" ")}` : ""}
    </span>
  );
}

/**
 * A scope marker's note, behind a disclosure that is CLOSED by default.
 *
 * ── Why this is no longer a paragraph ────────────────────────────────
 *
 * It used to render `meta.note` as running prose directly under the heading, and
 * those notes are long by design — `MARKERS.vendors` is five lines, `MARKERS.assets`
 * three — so nine screens opened with a block of small grey text above their own
 * content. Eden, 8 September 2026: *"get the wall of text off of the subheader
 * section of the pages it looks unprofessional."*
 *
 * **Nothing was deleted to achieve that.** The API still serves every marker, the
 * route tests still read them off `meta`, `ScopeTag` still carries the whole note
 * on `title`, and the text below is one click away. What changed is that the
 * screen's own content is now the first thing on it. A caveat nobody reads because
 * it is always there is worth less than one somebody opens on purpose — which is
 * the same reasoning `KpiCardTile` uses in the other direction for a per-figure
 * caveat, where the number and its qualification cannot be separated.
 *
 * The summary is deliberately one short line and never the note's first sentence:
 * a truncated caveat is the one shape that is worse than a hidden one, because it
 * reads as the whole answer.
 */
export function ScopeNote({ meta }: { meta: ScopeMarker | undefined }) {
  if (!meta?.note) return null;
  return (
    // The margin moved to `.scope-note` in industry.css when the screen-section
    // rhythm was named (18 September 2026): an inline style beats the rhythm
    // rule, so a section that keeps its own is a section that argues with it.
    <details className="scope-note">
      <summary
        className="text-muted"
        style={{ fontSize: "var(--tb-text-xs)", cursor: "pointer" }}
      >
        About this data
      </summary>
      <p
        className="text-muted"
        style={{ fontSize: "var(--tb-text-xs)", margin: "var(--space-2) 0 0", maxWidth: "78ch" }}
      >
        <ScopeTag meta={meta} /> {meta.note}
      </p>
    </details>
  );
}

/** Ids, money and timestamps are monospace throughout. */
export function Mono({
  children,
  size = "var(--tb-text-sm)",
  className = "",
}: {
  children: ReactNode;
  size?: number | string;
  className?: string;
}) {
  return (
    <span className={`mono ${className}`.trim()} style={{ fontSize: size }}>
      {children}
    </span>
  );
}

/**
 * Money, always from a decimal string.
 *
 * `amount` is typed as `string` and there is no `number` overload on purpose: a
 * component that accepted a number would be the place a `numeric(10,2)` silently
 * became a double.
 */
export function Money({
  amount,
  currency = "USD",
  size = "var(--tb-text-sm)",
  bold = false,
}: {
  amount: string;
  currency?: string;
  size?: number | string;
  bold?: boolean;
}) {
  const text = formatMoney(amount, currency);
  return (
    <span className="mono" style={{ fontSize: size, fontWeight: bold ? 700 : 400 }}>
      {text}
    </span>
  );
}

/**
 * Money that may be in more than one currency — listed, never added.
 *
 * There is no conversion rate anywhere in this system, so the sum of USD 308.05
 * and CAD 50.00 is not a number: the API used to compute one anyway and label it
 * with the alphabetically smaller code, and the screen rendered that as an
 * ordinary total. Every currency is shown, stacked, so a reader can see that two
 * things are being reported rather than one.
 *
 * The single-currency case — which is every row today — renders exactly as
 * `Money` did, so nothing changes for the ordinary reader.
 */
export function MoneyByCurrency({
  amounts,
  fallbackAmount,
  fallbackCurrency = "USD",
  size = "var(--tb-text-sm)",
  bold = false,
}: {
  amounts: ReadonlyArray<{ currency: string; totalAmount: string }>;
  /** Shown when the list is empty — a disclosed zero, not a withheld figure. */
  fallbackAmount: string;
  fallbackCurrency?: string;
  size?: number | string;
  bold?: boolean;
}) {
  if (amounts.length === 0) {
    return <Money amount={fallbackAmount} currency={fallbackCurrency} size={size} bold={bold} />;
  }
  if (amounts.length === 1) {
    const only = amounts[0]!;
    return (
      <Money amount={only.totalAmount} currency={only.currency} size={size} bold={bold} />
    );
  }
  return (
    <span
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}
      title={`Posted in ${amounts.length} currencies. Not added: there is no conversion rate.`}
    >
      {amounts.map((entry) => (
        <Money
          key={entry.currency}
          amount={entry.totalAmount}
          currency={entry.currency}
          size={size}
          bold={bold}
        />
      ))}
    </span>
  );
}

/**
 * A rate or unit cost — a **scale-4** value (`numeric(10,4)`).
 *
 * Separate from `Money` on purpose. `Money` parses at scale 2 and throws on a
 * third decimal place, which is correct for a ledger amount and wrong for a rate:
 * passing `posted_rate` to `Money` is what blanked the work-order screen. The two
 * components exist so the type of number is decided at the call site.
 */
export function Rate({
  rate,
  currency = "USD",
  suffix = "/h",
  size = "var(--tb-text-sm)",
}: {
  rate: string;
  currency?: string;
  suffix?: string;
  size?: number | string;
}) {
  return (
    <span className="mono" style={{ fontSize: size }}>
      {formatRate(rate, currency, suffix)}
    </span>
  );
}

/**
 * An "as of" stamp. The only sanctioned way to render a vendor-sourced value.
 *
 * A stale read renders with its age and the word STALE, and an unobserved one
 * renders "never seen" — never blank, and never a bare value that looks live.
 */
export function Freshness({
  observed,
  timeZone,
  className = "",
  staleAfterMs,
  referenceAt,
  snapshot = false,
  event = "opening",
}: {
  observed: Observed<unknown>;
  timeZone: string;
  className?: string;
  /**
   * How old the value may be and still read as current. Defaults to the
   * 30-minute vendor window; a GPS fix passes the five-minute position TTL so
   * this stamp and the Live map never disagree about the same fix.
   */
  staleAfterMs?: number;
  /** Server-provided classification clock for the affected dispatch/snapshot reads. */
  referenceAt?: string | null;
  /** Frozen at opening rather than aged against the time this screen is viewed. */
  snapshot?: boolean;
  /**
   * The event a snapshot is fixed at, in its words. Opening by default; the
   * Repair complete fix passes "repair complete" (TRBUD-430).
   */
  event?: SnapshotEvent;
}) {
  const label = snapshot
    ? formatSnapshotFreshness(observed.observedAt, referenceAt ?? null, staleAfterMs, event)
    : formatFreshness(observed.observedAt, referenceAt ? new Date(referenceAt) : new Date(), timeZone, staleAfterMs);
  return (
    <span
      className={`text-muted ${className}`.trim()}
      style={{ fontSize: "var(--tb-text-xs)" }}
      data-freshness={observed.freshness satisfies FreshnessState}
    >
      {label}
    </span>
  );
}

/**
 * The striped placeholder for a component that does not exist yet — a map, a
 * chart library, blob storage.
 *
 * Deliberately, obviously a placeholder. A plausible-looking fake chart invites
 * someone to read a trend off invented data; a labelled gap does not.
 */
export function Placeholder({
  label,
  height = 180,
  className = "",
}: {
  label: string;
  height?: number | string;
  className?: string;
}) {
  return (
    <div className={`placeholder ${className}`.trim()} style={{ height }}>
      <span>{label}</span>
    </div>
  );
}

/** A label/value pair for the attribute strips. */
export function Attribute({
  label,
  children,
  mono = false,
  title,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
  /** A hover note saying what the figure counts, e.g. `SPEND_YTD_NOTE`. */
  title?: string;
}) {
  return (
    /*
     * `attribute` earns its class by being the one place a vendor's own free text
     * is rendered at full width — TRBUD-272. Eden found a 500-character phone
     * "number" running out of the side of a vendor card here, because the value
     * was a bare `<strong>` with nothing telling the browser it may break an
     * unbroken token. The wrapping rule is in `industry.css` beside the others.
     */
    <span className="attribute" style={{ fontSize: "var(--tb-text-sm)" }} title={title}>
      <span className="text-muted">{label}</span>
      <br />
      <strong className={mono ? "mono" : undefined}>{children}</strong>
    </span>
  );
}

/** A dash, so an empty cell reads as "no value" rather than as a rendering bug. */
export function Empty() {
  return <span className="text-muted">—</span>;
}
